import { client, withDataRequest } from 'utils/client';
import { DataResponse, GetSelectList, SelectItem, GetSelectListResponse } from 'api/types';
import { TagFilters, GetMyProgramsTable, ProgramFormData, MyProgramTableView } from 'features/programs/types';
import { ProgramDataForContext } from 'features/selected-context/types';
import { GetProgramConnectionsTable, EmployerDiscoverProgramTableView } from 'features/explore-programs/types';
import { GetJobSeekerProgramConnectionsTable, JobSeekerDiscoverProgramTableView } from 'features/job-seeker-explore-programs/types';
import { GetJoinSelectList } from 'features/network-join';
import { TableDataResponse } from 'features/types';
import { DiscoverProgramTableView, GetDiscoverProgramsTable } from 'features/discover-programs';
import { ProgramsFiltersData } from 'features/programs-filters';
import { Tab } from 'api/types';
import { GetProgramSelectorSelectList, ProgramSelectorTableView } from 'features/programs/types';
import { ProgramDetailsData } from 'features/program-details';

const programsAPI = {
  getProgramTable(params: GetMyProgramsTable) {
    return client.post<TableDataResponse<MyProgramTableView>>('/programs/get-table', withDataRequest(params!));
  },
  addProgram(data: FormData) {
    return client.post<void>('/programs/add', withDataRequest(data));
  },
  getFormData() {
    return client.get<ProgramFormData>('/programs/form-data');
  },
  getEditFormData(id: string) {
    return client.get<ProgramFormData>(`/programs/${id}/form-data`);
  },
  editFormData(id: string, data: FormData) {
    return client.put<void>(`/programs/${id}/edit`, withDataRequest(data));
  },
  getSelectList(params: GetSelectList) {
    return client.post<DataResponse<SelectItem>>(`/programs/get-select-list`, withDataRequest(params));
  },
  getSelectListForLeadJobSeeker(params: GetSelectList & { onBehalfOfOrganizationId?: string; }) {
    return client.post<DataResponse<SelectItem>>(`/programs/get-select-list-for-lead-jobseeker`, withDataRequest(params));
  },
  getOrganizationProgramSelectorSelectList(params: GetProgramSelectorSelectList) {
    return client.post<TableDataResponse<ProgramSelectorTableView> | DataResponse<SelectItem>>(`/programs/get-organization-job-program-selector-select-list`, withDataRequest(params));
  },
  getOrganizationProgramSelectorTabsData(params: GetProgramSelectorSelectList) {
    return client.post<Tab[]>('/programs/organization-program-selector-tabs', withDataRequest(params));
  },
  getEmployerSelectList(params: GetSelectList) {
    return client.post<GetSelectListResponse>(`/programs/get-employer-select-list`, withDataRequest(params));
  },
  getEmployerProgramSelectorSelectList(params: GetProgramSelectorSelectList) {
    return client.post<TableDataResponse<ProgramSelectorTableView> | DataResponse<SelectItem>>(`/programs/get-employer-job-program-selector-select-list`, withDataRequest(params));
  },
  getEmployerProgramSelectorTabsData(params: GetProgramSelectorSelectList) {
    return client.post<Tab[]>('/programs/employer-program-selector-tabs', withDataRequest(params));
  },
  getTags(params: TagFilters) {
    return client.post<DataResponse<SelectItem>>('/tags/get-select-list', withDataRequest(params));
  },
  getProgramTags(params: TagFilters) {
    return client.post<DataResponse<SelectItem>>('/program-tags/get-select-list', withDataRequest(params));
  },
  getTagsForLeadJobSeeker(params: TagFilters & { onBehalfOfOrganizationId?: string; }) {
    return client.post<DataResponse<SelectItem>>('/tags/get-select-list', withDataRequest(params));
  },
  getEmployerTable(params: GetProgramConnectionsTable) {
    return client.post<TableDataResponse<EmployerDiscoverProgramTableView>>(`/programs/get-employer-table`, withDataRequest(params));
  },
  getJobSeekerTable(params: GetJobSeekerProgramConnectionsTable) {
    return client.post<TableDataResponse<JobSeekerDiscoverProgramTableView>>(`/programs/get-job-seeker-table`, withDataRequest(params));
  },
  getJobSeekerTabsData(params: GetJobSeekerProgramConnectionsTable) {
    return client.post<Tab[]>('/programs/job-seeker-tabs', withDataRequest(params));
  },
  getEmployerFilters() {
    return client.get<ProgramsFiltersData>(`/programs/employer-filters`);
  },
  getJoinSelectList(params: GetJoinSelectList) {
    return client.post<GetSelectListResponse>(`/programs/get-join-select-list`, withDataRequest(params));
  },
  getSpecificJobsSelectList(params: GetSelectList) {
    return client.post<DataResponse<SelectItem>>('/programs/get-specific-jobs-select-list', withDataRequest(params));
  },
  getProgramSpecificJobsSelectList(params: GetSelectList) {
    return client.post<DataResponse<SelectItem>>('/programs/get-program-specific-jobs-select-list', withDataRequest(params));
  },
  getDiscoverTable(params: GetDiscoverProgramsTable) {
    return client.post<TableDataResponse<DiscoverProgramTableView>>(`/programs/get-discover-table`, withDataRequest(params));
  },
  getJobSeekerAllowedProgramsSelectList(params: GetSelectList) {
    return client.post<GetSelectListResponse>(`/programs/get-jobseeker-allowed-programs-select-list`, withDataRequest(params));
  },
  getJobSeekerCanInviteToProgramsSelectList(params: GetSelectList) {
    return client.post<GetSelectListResponse>(`/programs/get-jobseeker-invite-others-select-list`, withDataRequest(params));
  },
  getJobSeekerCanPostProgramsSelectList(params: GetSelectList) {
    return client.post<GetSelectListResponse>(`/programs/get-jobseeker-can-post-select-list`, withDataRequest(params));
  },
  getJobSeekerProgramSelectorSelectList(params: GetProgramSelectorSelectList) {
    return client.post<TableDataResponse<ProgramSelectorTableView> | DataResponse<SelectItem>>(`/programs/get-jobseeker-job-program-selector-select-list`, withDataRequest(params));
  },
  getJobSeekerProgramSelectorTabsData(params: GetProgramSelectorSelectList) {
    return client.post<Tab[]>('/programs/jobseeker-program-selector-tabs', withDataRequest(params));
  },
  getJobSeekerCanPostResourceProgramsSelectList(params: GetSelectList) {
    return client.post<GetSelectListResponse>(`/programs/get-jobseeker-can-post-resource-select-list`, withDataRequest(params));
  },
  getJobSeekerResourceProgramSelectorSelectList(params: GetProgramSelectorSelectList) {
    return client.post<TableDataResponse<ProgramSelectorTableView> | DataResponse<SelectItem>>('/programs/get-jobseeker-resource-program-selector-select-list', withDataRequest(params));
  },
  getJobSeekerResourceProgramSelectorTabsData(params: GetProgramSelectorSelectList) {
    return client.post<Tab[]>('/programs/jobseeker-resource-program-selector-tabs', withDataRequest(params));
  },
  getJobSeekerCanViewOtherMembersInProgramsSelectList(params: GetSelectList) {
    return client.post<GetSelectListResponse>('/programs/get-jobseeker-can-view-others-select-list', withDataRequest(params));
  },
  getProgramDataForContext(id: string) {
    return client.get<ProgramDataForContext>(`/programs/${id}/get-program-data-for-context`);
  },
  getProgramDataForAboutPage(id: string) {
    return client.get<ProgramDetailsData>(`/programs/${id}/get-program-data-for-about-page`);
  },
  getAdminMemberProgramsSelectList(params: GetSelectList) {
    return client.post<GetSelectListResponse>('/programs/get-admin-member-programs-select-list', withDataRequest(params));
  },
  getAdminMemberTable(params: GetMyProgramsTable) {
    return client.post<TableDataResponse<MyProgramTableView>>('/programs/get-admin-member-table', withDataRequest(params));
  },
  getAdminMemberFormData() {
    return client.get<ProgramFormData>('/programs/admin-member-form-data');
  },
  getAdminMemberEditFormData(id: string) {
    return client.get<ProgramFormData>(`/programs/${id}/admin-member-form-data`);
  },
  adminMemberAddProgram(data: FormData) {
    return client.post<void>('/programs/admin-member-add', withDataRequest(data));
  },
  adminMemberEditProgram(id: string, data: FormData) {
    return client.put<void>(`/programs/${id}/admin-member-edit`, withDataRequest(data));
  },
};

export default programsAPI;